import { css, type SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  styleguideElevation as ELEVATION,
  styleguideFontFamily as FONT,
  styleguideLetterSpacing as LETTER_SPACING,
  styleguideSpacing as SPACING,
} from '@kaiizen/kai-shiki/core';

export type MarketPanelStyles = {
  drawer: SerializedStyles;
  header: SerializedStyles;
  content: SerializedStyles;
  closeButton: SerializedStyles;
  title: SerializedStyles;
  titleEyebrow: SerializedStyles;
  titleName: SerializedStyles;
  form: SerializedStyles;
  section: SerializedStyles;
  sectionFirst: SerializedStyles;
  sectionLast: SerializedStyles;
  sectionTitle: SerializedStyles;
  field: SerializedStyles;
  fieldLabel: SerializedStyles;
  optionalIndicator: SerializedStyles;
  footer: SerializedStyles;
  actions: SerializedStyles;
  cancelAction: SerializedStyles;
  submitAction: SerializedStyles;
};

export const DEFAULT_MARKET_PANEL_STYLES: MarketPanelStyles = {
  drawer: css`
    label: MARKET_PANEL_DRAWER;
    width: calc(${SPACING.s8}px * 6 + ${SPACING.s3}px * 2 + ${SPACING.s2}px);
    max-width: 90vw;
    padding: 0;
    border-left-color: ${COLORS.stroke};
    background: ${COLORS.paper};
    box-shadow: ${ELEVATION.modal};
  `,
  header: css`
    label: MARKET_PANEL_HEADER;
    min-height: calc(${SPACING.s8}px + ${SPACING.s3}px);
    padding: ${SPACING.s4}px ${SPACING.s5}px;
    align-items: flex-start;
    border-bottom-color: ${COLORS.stroke};
  `,
  content: css`
    label: MARKET_PANEL_CONTENT;
    gap: 0;
    padding: 0 ${SPACING.s5}px;
    background: ${COLORS.paper};
  `,
  closeButton: css`
    label: MARKET_PANEL_CLOSE_BUTTON;
    width: calc(${SPACING.s6}px + ${SPACING.s1}px) !important;
    height: calc(${SPACING.s6}px + ${SPACING.s1}px) !important;
    margin: 0;
    border: 1px solid ${COLORS.stroke} !important;
    background: ${COLORS.paper} !important;
    color: ${COLORS.textSecondary} !important;

    &:hover {
      border-color: ${COLORS.ink} !important;
      color: ${COLORS.ink} !important;
    }
  `,
  title: css`
    label: MARKET_PANEL_TITLE;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${SPACING.s1}px;
  `,
  titleEyebrow: css`
    label: MARKET_PANEL_TITLE_EYEBROW;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoLabel)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoCaption};
    line-height: 1.2;
    text-transform: uppercase;
  `,
  titleName: css`
    label: MARKET_PANEL_TITLE_NAME;
    color: ${COLORS.ink};
    font-family: ${FONT.display};
    font-size: ${pxToRem(FONT_SIZE.displayMedium)};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.1;
  `,
  form: css`
    label: MARKET_PANEL_FORM;
    display: flex;
    flex-direction: column;
  `,
  section: css`
    label: MARKET_PANEL_SECTION;
    display: flex;
    flex-direction: column;
    gap: ${SPACING.s4}px;
    padding: ${SPACING.s5}px 0;
    border-bottom: 1px solid ${COLORS.stroke};
  `,
  sectionFirst: css`
    label: MARKET_PANEL_SECTION_FIRST;
    padding-top: ${SPACING.s5}px;
  `,
  sectionLast: css`
    label: MARKET_PANEL_SECTION_LAST;
    border-bottom: 0;
  `,
  sectionTitle: css`
    label: MARKET_PANEL_SECTION_TITLE;
    margin: 0;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoLabel)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoCaption};
    line-height: 1.2;
    text-transform: uppercase;
  `,
  field: css`
    label: MARKET_PANEL_FIELD;
    display: flex;
    flex-direction: column;
    gap: ${SPACING.s2}px;
  `,
  fieldLabel: css`
    label: MARKET_PANEL_FIELD_LABEL;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoLabel)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoTight};
    line-height: 1.2;
    text-transform: uppercase;
  `,
  optionalIndicator: css`
    label: MARKET_PANEL_OPTIONAL_INDICATOR;
    margin-left: ${SPACING.s2}px;
    color: ${COLORS.midtone};
    font-weight: ${FONT_WEIGHT.regular};
  `,
  footer: css`
    label: MARKET_PANEL_FOOTER;
    display: flex;
    justify-content: flex-end;
    padding: ${SPACING.s4}px ${SPACING.s5}px;
    border-top-color: ${COLORS.stroke};
    background: ${COLORS.paper};
  `,
  actions: css`
    label: MARKET_PANEL_ACTIONS;
    display: flex;
    gap: ${SPACING.s2}px;
  `,
  cancelAction: css`
    label: MARKET_PANEL_CANCEL_ACTION;
    min-width: calc(${SPACING.s8}px * 2 + ${SPACING.s2}px);
    padding: ${SPACING.s3}px ${SPACING.s4}px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
    color: ${COLORS.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoLabel)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoLabel};
    text-transform: uppercase;

    &:hover {
      border-color: ${COLORS.ink};
    }
  `,
  submitAction: css`
    label: MARKET_PANEL_SUBMIT_ACTION;
    min-width: calc(${SPACING.s8}px * 2 + ${SPACING.s5}px);
    padding: ${SPACING.s3}px ${SPACING.s4}px;
    border: 1px solid ${COLORS.accentDeep};
    background: ${COLORS.accent};
    color: ${COLORS.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoLabel)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoLabel};
    text-transform: uppercase;

    &:hover:not(:disabled) {
      border-color: ${COLORS.ink};
      background: ${COLORS.accentDeep};
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  `,
};
